import { useLocalization } from 'src/shared/hooks/useLocalization'
import type { LogoFileType } from './types'

/** Extensions we allow for vector / production logo uploads */
const LOGO_EXTENSIONS: ReadonlySet<string> = new Set([
  'svg',
  'png',
  'jpg',
  'jpeg',
  'pdf',
  'eps',
  'ai',
])

export const isAllowedVectorLogoExtension = (ext: string): boolean =>
  LOGO_EXTENSIONS.has(ext.toLowerCase())

/** Maps stored filename extensions to branded logo types (unknown falls back so listing never breaks). */
export const extToLogoFileType = (ext: string): LogoFileType => {
  const lower = ext.toLowerCase()
  return (LOGO_EXTENSIONS.has(lower) ? lower : 'png') as LogoFileType
}

export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
}

export const useFormatDate = () => {
  const { t } = useLocalization()

  return (dateString: string): string => {
    const date = new Date(dateString)
    const now = new Date()
    const diffTime = Math.abs(now.getTime() - date.getTime())
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))

    if (diffDays === 0) return t('Today', 'Idag')
    if (diffDays === 1) return t('Yesterday', 'Igår')
    if (diffDays < 7) return t('{{days}} days ago', `${diffDays} dagar sedan`)
    if (diffDays < 30) {
      const weeks = Math.floor(diffDays / 7)
      return t('{{weeks}} weeks ago', `${weeks} veckor sedan`)
    }
    if (diffDays < 365) {
      const months = Math.floor(diffDays / 30)
      return t('{{months}} months ago', `${months} månader sedan`)
    }
    const years = Math.floor(diffDays / 365)
    return t('{{years}} years ago', `${years} år sedan`)
  }
}
